import { useTranslation } from 'react-i18next';
import { useDebouncedLoader } from 'use-debounced-loader';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import RotatingRingIcon from '@/shared/components/Button/RotatingRingIcon';

import socialLinkButtonStyles from './SocialLinkButton.module.scss';

export type Props = {
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
  readonly className?: string;
  readonly target: string;
  readonly logo: string;
  readonly name: Record<string, string>;
  readonly onClick?: () => void;
};

const SocialLinkButton = ({
  isDisabled,
  isLoading = false,
  className,
  name,
  logo,
  onClick,
}: Props) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const localName = name[language] ?? name.en;

  const isLoadingActive = useDebouncedLoader(isLoading, 300);

  return (
    <Button
      disabled={isDisabled}
      variant="outline"
      type="button"
      className={cn(
        'group inline-flex h-11 w-full items-center justify-center gap-3 rounded-md border border-border bg-white px-4 text-sm font-medium text-card-foreground shadow-xs transition-colors hover:bg-muted/60',
        (isDisabled ?? isLoadingActive) && 'opacity-50 pointer-events-none',
        className
      )}
      onClick={onClick}
      aria-label={t('action.sign_in_with', { name: localName })}
    >
      {logo && !isLoadingActive && (
        <img src={logo} alt="" aria-hidden className={cn('size-4 object-contain')} />
      )}
      {isLoadingActive && (
        <span className={socialLinkButtonStyles.loadingIcon}>
          <RotatingRingIcon />
        </span>
      )}
      <span>{t('action.sign_in_with', { name: localName })}</span>
    </Button>
  );
};

export default SocialLinkButton;
